/**
 * Shared by the Hr page (company copies) and the Operator catalogue page. Numbers are kept as strings while
 * the user types so an emptied field is not silently read as 0; `readStatutoryDraft` turns the draft into the
 * request body and reports what is wrong.
 */

export interface StatutoryValues {
  leaveTypeName: string;
  minDaysPerYear: number;
  carryForwardAllowed: boolean;
  maxCarryForwardDays: number | null;
  probationMonths: number;
  notes: string | null;
}

export interface StatutoryDraft {
  label: string;
  minDays: string;
  carryForward: boolean;
  maxCarry: string;
  probation: string;
  notes: string;
}

export type StatutoryDraftErrors = Partial<Record<keyof StatutoryDraft, string>>;

/** The server's limit on the statutory name (CopyLabelMaxLength). */
export const STATUTORY_LABEL_MAX = 200;

export function toStatutoryDraft(values: StatutoryValues): StatutoryDraft {
  return {
    label: values.leaveTypeName,
    minDays: String(values.minDaysPerYear),
    carryForward: values.carryForwardAllowed,
    maxCarry: values.maxCarryForwardDays == null ? '' : String(values.maxCarryForwardDays),
    probation: String(values.probationMonths),
    notes: values.notes ?? '',
  };
}

export const emptyStatutoryDraft: StatutoryDraft = {
  label: '', minDays: '', carryForward: false, maxCarry: '', probation: '0', notes: '',
};

/**
 * Validates the draft the way the server does (label 1..200, no negative days or months) and returns the values.
 * The label is sent exactly as typed when it has no outer spaces, so an untouched copy compares equal on the
 * server and a link-only edit does not mark it customized.
 */
export function readStatutoryDraft(
  draft: StatutoryDraft,
  t: (key: string, opts?: Record<string, unknown>) => string,
): { values: StatutoryValues | null; errors: StatutoryDraftErrors } {
  const errors: StatutoryDraftErrors = {};
  const label = draft.label.trim();
  if (!label) errors.label = t('leaves:statutoryTemplates.errorLabelRequired');
  else if (label.length > STATUTORY_LABEL_MAX) errors.label = t('leaves:statutoryTemplates.errorLabelTooLong', { max: STATUTORY_LABEL_MAX });

  const minDays = Number(draft.minDays);
  if (draft.minDays.trim() === '' || !Number.isFinite(minDays) || minDays < 0) {
    errors.minDays = t('leaves:statutoryTemplates.errorNotNegative');
  }
  const probation = Number(draft.probation);
  if (draft.probation.trim() === '' || !Number.isInteger(probation) || probation < 0) {
    errors.probation = t('leaves:statutoryTemplates.errorWholeMonths');
  }
  // The cap is only read while carry forward is on, but an unchanged cap is sent back as it was, so an edit that
  // does not touch it never counts as a change on the server.
  const maxCarry: number | null = draft.maxCarry.trim() === '' ? null : Number(draft.maxCarry);
  if (draft.carryForward && maxCarry != null && (!Number.isFinite(maxCarry) || maxCarry < 0)) {
    errors.maxCarry = t('leaves:statutoryTemplates.errorNotNegative');
  }
  if (Object.keys(errors).length > 0) return { values: null, errors };

  const notes = draft.notes.trim() === '' ? null : draft.notes;
  return {
    values: {
      leaveTypeName: label,
      minDaysPerYear: minDays,
      carryForwardAllowed: draft.carryForward,
      maxCarryForwardDays: maxCarry != null && Number.isFinite(maxCarry) ? maxCarry : null,
      probationMonths: probation,
      notes,
    },
    errors,
  };
}

/** Days are stored as floats (half days exist). Show 30, not 30.0, and 2.5 as is. */
export function formatStatutoryNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
}
